import React, {
  useId,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { defineMessages, useIntl, IntlShape } from "react-intl";
import classnames from "classnames";
import * as Form from "@radix-ui/react-form";
import { Pencil } from "@labxchange/icons";

import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import { BodyText } from "../BodyText";
import { TooltipProvider } from "../Tooltip";

import { LxButton } from "../LxButton";
import { ActionAnnouncer } from "../ActionAnnouncer";
import { useStatusAnnouncer, useSpokenText } from "../StatusAnnouncer";
import { Collapsible } from "../LxCollapsible";
import { Figure } from "../Figure";
import { FeedbackBanner } from "../FeedbackBanner";
import { useTableQuestion } from "./useTableQuestion";

import { fetchTranslations } from "./locales/config";
import {
  TableQuestionProps,
  TableCellData,
  CellChangeParams,
  ButtonClickParams,
  AnswerType,
} from "./types";
import { renderTableCell } from "./cellRenderers";
import {
  tableQuestionClass,
  tableReadonlyClass,
  tableQuestionTransparentClass,
  containerClass,
  containerReadonlyClass,
  containerTransparentClass,
  questionCategoryClass,
  questionHeaderClass,
  questionTitleClass,
  questionTextClass,
  questionTextParagraphClass,
  tableWrapperClass,
  tableClass,
  tableRowClass,
  tableTopAlignClass,
  footerClass,
  attemptsTextClass,
  hintAreaClass,
  hintTextClass,
  instructionTextClass,
  collapsibleClass,
  feedbackMessagesContainerClass,
  liveRegionClass,
  liveRegionVisibleClass,
} from "./TableQuestion.css";

/* -------------------------------- Messages -------------------------------- */

export const messages = defineMessages({
  tableQuestionHeader: {
    id: "TableQuestion.header",
    defaultMessage: "Table Question",
    description: "Header text for table question type.",
  },
  predictionHeader: {
    id: "TableQuestion.predictionHeader",
    defaultMessage: "Prediction Table",
    description: "Header text for prediction table type.",
  },
  attemptsLeft: {
    id: "TableQuestion.attemptsLeft",
    defaultMessage: "Attempts left: {attemptsLeft}",
    description: "Label showing the number of attempts left.",
  },
  submitAnswer: {
    id: "TableQuestion.submitAnswer",
    defaultMessage: "Submit answer",
    description: "Text for the submit button.",
  },
  submitPrediction: {
    id: "TableQuestion.submitPrediction",
    defaultMessage: "Submit prediction",
    description: "Text for the submit prediction button.",
  },
  correctFeedbackDefault: {
    id: "TableQuestion.correctFeedbackDefault",
    defaultMessage: "Correct!",
    description: "Default feedback for correct answer.",
  },
  incorrectFeedbackDefault: {
    id: "TableQuestion.incorrectFeedbackDefault",
    defaultMessage:
      "Not quite. {incorrectCount}/{totalCount} answers are incorrect. Try again.",
    description: "Default feedback for incorrect answer.",
  },
  predictionSubmitted: {
    id: "TableQuestion.predictionSubmitted",
    defaultMessage:
      "Prediction submitted. You will compare this answer with your final result.",
    description: "Text shown when prediction is submitted.",
  },
  showHint: {
    id: "TableQuestion.showHint",
    defaultMessage: "Show hint",
    description: "Text for the show hint button.",
  },
  nextHint: {
    id: "TableQuestion.nextHint",
    defaultMessage: "Next hint",
    description: "Text for the next hint button.",
  },
  resetPrediction: {
    id: "TableQuestion.resetPrediction",
    defaultMessage: "Reset prediction",
    description: "Text for the reset prediction button.",
  },
  instructionText: {
    id: "TableQuestion.instructionText",
    defaultMessage:
      "(Complete the table by entering the correct information into each cell.)",
    description: "Instruction text displayed after the question",
  },
  incompleteWarning: {
    id: "TableQuestion.incompleteWarning",
    defaultMessage: "Please fill in all cells before submitting.",
    description:
      "Warning shown when submit is clicked while some cells are empty.",
  },
  cellLabelColumnRow: {
    id: "TableQuestion.cellLabelColumnRow",
    defaultMessage: "{column}, {row}",
    description:
      "Accessible label for an editable table cell, combining its column header and row label.",
  },
  cellLabelColumnOnly: {
    id: "TableQuestion.cellLabelColumnOnly",
    defaultMessage: "{column}, row {rowNumber}",
    description:
      "Accessible label for an editable table cell when only a column header is available.",
  },
  cellLabelFallback: {
    id: "TableQuestion.cellLabelFallback",
    defaultMessage: "Row {rowNumber}, column {columnNumber}",
    description:
      "Accessible label fallback for an editable table cell when no header text is available.",
  },
});

/* -------------------------------- Helper Functions -------------------------------- */

const containsMathJax = (text: string | undefined): boolean => {
  if (!text || typeof text !== "string") return false;
  return /\$\$.*?\$\$|\$.*?\$/g.test(text);
};

const hasEmptyEditableCells = (data: TableCellData[][]): boolean => {
  for (const row of data) {
    for (const cell of row) {
      if (cell.type === "INPUT" || cell.type === "DROPDOWN") {
        if (
          cell.value === null ||
          cell.value === undefined ||
          cell.value === ""
        ) {
          return true;
        }
      }
    }
  }
  return false;
};

const hasInteractiveCells = (data: TableCellData[][]): boolean => {
  for (const row of data) {
    for (const cell of row) {
      if (
        cell.type === "INPUT" ||
        cell.type === "DROPDOWN" ||
        cell.type === "BUTTON"
      ) {
        return true;
      }
    }
  }
  return false;
};

const validateCellAnswer = (
  value: string | number | null | undefined,
  answer: AnswerType | undefined,
  accuracy?: number
): boolean => {
  if (value === null || value === undefined || value === "") return false;
  if (answer === undefined) return true;

  if (typeof answer === "string") {
    return answer.toLowerCase() === String(value).toLowerCase();
  }

  if (typeof answer === "number") {
    const numValue = Number(value);
    if (accuracy !== undefined) {
      return Math.abs(numValue - answer) < Math.pow(10, -accuracy);
    }
    return numValue === answer;
  }

  if (typeof answer === "object" && answer !== null && !Array.isArray(answer)) {
    const numValue = Number(value);
    const { min, max } = answer;
    if (min !== undefined && numValue < min) return false;
    if (max !== undefined && numValue > max) return false;
    return true;
  }

  if (Array.isArray(answer)) {
    return answer.some((a) => {
      if (typeof a === "string") {
        return a.toLowerCase() === String(value).toLowerCase();
      }
      return a === Number(value);
    });
  }

  return false;
};

const countAnswerableCells = (
  data: TableCellData[][]
): { totalCount: number; incorrectCount: number } => {
  let totalCount = 0;
  let incorrectCount = 0;

  for (const row of data) {
    for (const cell of row) {
      if (cell.answer !== undefined) {
        totalCount++;
        const isCorrect = validateCellAnswer(
          cell.value,
          cell.answer,
          cell.accuracy
        );
        if (!isCorrect) {
          incorrectCount++;
        }
      }
    }
  }

  return { totalCount, incorrectCount };
};

const stripHtml = (text: string): string =>
  text
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Build an accessible name for an editable cell so screen-reader users know
 * which value they are entering. Combines the column header with a row label
 * derived from the row's first descriptive cell, falling back to positional
 * text when the table has no header/label cells.
 *
 * A source cell's authored `ariaLabel` wins over its `text`, so labelling a
 * symbol cell once also fixes the names composed from it — otherwise a row
 * headed by "*" would contribute a character the screen reader skips.
 */
const getCellAccessibleLabel = (
  data: TableCellData[][],
  rowIndex: number,
  colIndex: number,
  intl: IntlShape
): string => {
  // Translate before stripping: a translated string can carry the same markup
  // `text` does, and the composed name is spoken, so it must be localized just
  // like the value the cell renderers put on `aria-label`.
  const translate = (value?: string): string | undefined => {
    if (!value) return undefined;
    return (
      stripHtml(intl.formatMessage({ id: value, defaultMessage: value })) ||
      undefined
    );
  };

  // Column header: the first row made entirely of TITLE cells whose width
  // matches the widest (body) rows — i.e. the real column-header row, not a
  // single spanning section-title row.
  const bodyWidth = Math.max(0, ...data.map((row) => row.length));
  const headerRow = data.find(
    (row) =>
      row.length === bodyWidth &&
      bodyWidth > 1 &&
      row.every((cell) => cell.type === "TITLE")
  );
  const columnHeader = translate(
    headerRow?.[colIndex]?.ariaLabel ?? headerRow?.[colIndex]?.text
  );

  // Row label: use the first text-bearing non-editable cell in the row.
  // TITLE, LIST, and FIXED cells can all serve as row identifiers.
  let rowLabel: string | undefined;
  for (const cell of data[rowIndex] ?? []) {
    if (
      cell.type === "TITLE" ||
      cell.type === "LIST" ||
      cell.type === "FIXED"
    ) {
      rowLabel = translate(cell.ariaLabel ?? cell.text);
      if (rowLabel) break;
    }
  }

  if (columnHeader && rowLabel) {
    return intl.formatMessage(messages.cellLabelColumnRow, {
      column: columnHeader,
      row: rowLabel,
    });
  }
  if (columnHeader) {
    return intl.formatMessage(messages.cellLabelColumnOnly, {
      column: columnHeader,
      rowNumber: rowIndex + 1,
    });
  }
  return intl.formatMessage(messages.cellLabelFallback, {
    rowNumber: rowIndex + 1,
    columnNumber: colIndex + 1,
  });
};

/* -------------------------------- Sub Components -------------------------------- */

interface QuestionHeaderProps {
  id: string;
  isPrediction: boolean;
  heading?: string;
}

const QuestionHeader: React.FC<QuestionHeaderProps> = ({
  id,
  isPrediction,
  heading,
}) => {
  const { formatMessage } = useIntl();

  const headerText =
    heading ||
    formatMessage(
      isPrediction ? messages.predictionHeader : messages.tableQuestionHeader
    );

  return (
    <p id={id} className={questionCategoryClass}>
      {headerText}
    </p>
  );
};

/* -------------------------------- Main Component -------------------------------- */

const TableQuestionInternal: React.FC<TableQuestionProps> = ({
  id,
  type,
  data,
  content = ["title", "text", "table"],
  title,
  text,
  question,
  heading,
  image,
  questionType,
  totalAttempts = -1,
  isSubmitted: isSubmittedProp = false,
  submitDisabled,
  buttonText,
  iconName,
  hideFooter,
  hideAttemptsLeft,
  showQuestionHeading = false,
  hintDetails,
  hints,
  hintIndex = -1,
  showHint,
  correctFeedback,
  incorrectFeedback,
  correct,
  feedbackMessages,
  onCellChange,
  onButtonClick,
  submitAnswer,
  setShowHint,
  showNextHint,
  setCompleted,
  onReset,
  className,
  striped,
  isTransparent = false,
  verticalAlign,
  showFeedback = true,
}) => {
  const intl = useIntl();
  const componentId = useId();
  const headerId = `${componentId}-header`;

  const [reduxState, tableActions] = useTableQuestion(id);
  const {
    attemptsLeft: reduxAttemptsLeft,
    modifiedCells: reduxModifiedCells,
    cellValues: reduxCellValues,
  } = reduxState;

  // Initialize state from Redux if available, otherwise use default values
  const [attemptsLeft, setAttemptsLeft] = useState<number>(
    reduxAttemptsLeft ??
      (typeof totalAttempts === "string"
        ? parseInt(totalAttempts, 10)
        : totalAttempts)
  );
  // Use component's isSubmittedProp as the source of truth, not Redux
  // This prevents shared Redux state from affecting other table instances
  const [isSubmitted, setIsSubmitted] = useState(isSubmittedProp);
  const [modifiedCells, setModifiedCells] = useState<Set<string>>(
    new Set(reduxModifiedCells ?? [])
  );
  // Track correct state locally so resubmissions update the feedback banner
  // even when the parent doesn't manage the correct prop (e.g. hotspot sims)
  const [localCorrect, setLocalCorrect] = useState<boolean | null | undefined>(
    correct
  );
  // Submit stays enabled (matching the other question types); when it is
  // clicked with empty editable cells we surface a warning and flag those
  // cells instead of blocking submission via a disabled button.
  const [isIncomplete, setIsIncomplete] = useState(false);

  // Question-level result readout. `useStatusAnnouncer` owns the two mounted
  // polite regions and the ping-pong write that makes even a byte-identical
  // repeat announce; see that hook for why this, and not the visible banner,
  // is the announcement channel.
  //
  // The whole result (verdict + any parent-supplied feedback messages +
  // remaining attempts) goes out as one message, because announcing each piece
  // from its own live region is what made them compete and drop.
  const {
    announce,
    clear: clearRegions,
    regions: announcementRegions,
  } = useStatusAnnouncer("table-question-result");
  const spokenText = useSpokenText();

  // A prediction submit moves focus to Reset in the same commit that would
  // otherwise write the region (see the `showReset` effect). The result waits
  // here until that focus move has happened, so the focus announcement cannot
  // discard it.
  const pendingResultRef = useRef<string | null>(null);

  const clearResult = useCallback(() => {
    pendingResultRef.current = null;
    clearRegions();
  }, [clearRegions]);

  useEffect(() => {
    setLocalCorrect(correct);
  }, [correct]);

  // Initialize table in Redux on mount
  useEffect(() => {
    if (reduxAttemptsLeft === undefined) {
      tableActions.initialize({
        attemptsLeft:
          typeof totalAttempts === "string"
            ? parseInt(totalAttempts, 10)
            : totalAttempts,
      });
    }
  }, [reduxAttemptsLeft, totalAttempts, tableActions]);

  // Merge redux cell values into data
  const dataWithReduxValues = useMemo(() => {
    if (!reduxCellValues) return data;

    return data.map((row, rowIndex) =>
      row.map((cell, colIndex) => {
        const cellKey = `${rowIndex}_${colIndex}`;
        const reduxValue = reduxCellValues[cellKey];

        if (
          reduxValue !== undefined &&
          (cell.type === "INPUT" || cell.type === "DROPDOWN")
        ) {
          const isDefaultValue =
            cell.value === undefined ||
            cell.value === null ||
            cell.value === "";

          if (isDefaultValue) {
            return { ...cell, value: reduxValue };
          }
        }

        return cell;
      })
    );
  }, [data, reduxCellValues]);

  const isPrediction = questionType === "prediction";
  const isDataTable = type === "DATA_TABLE";
  const isReadOnly = isDataTable && !hasInteractiveCells(dataWithReduxValues);

  // Once a prediction is submitted, the Submit button is replaced by the Reset
  // button. Move focus to the replacement so keyboard users aren't dropped to
  // <body> when the element they activated is removed (WCAG 2.4.3).
  const showReset =
    isPrediction &&
    localCorrect !== null &&
    localCorrect !== undefined &&
    !!onReset;
  const footerRef = useRef<HTMLDivElement>(null);
  const prevShowResetRef = useRef(showReset);
  // Only a submit made *here* should move focus. `localCorrect` also mirrors the
  // `correct` prop, so a parent flipping it after mount (restoring saved
  // progress, say) would otherwise pull focus with no user action behind it.
  const submittedHereRef = useRef(false);
  useEffect(() => {
    if (showReset && !prevShowResetRef.current && submittedHereRef.current) {
      footerRef.current
        ?.querySelector<HTMLButtonElement>('[data-testid="reset-prediction"]')
        ?.focus();
      // Announce only now. Writing the region during the submit handler would
      // put the text change *before* this focus event, which is the ordering
      // that let the focus announcement swallow the old alert; announcing after
      // makes it a fresh change the focus announcement cannot pre-empt.
      if (pendingResultRef.current !== null) {
        announce(pendingResultRef.current);
        pendingResultRef.current = null;
      }
    }
    prevShowResetRef.current = showReset;
  }, [showReset, announce]);

  const checkAllAnswersCorrect = useCallback((): boolean => {
    let allCorrect = true;
    const uniqueValues: (string | number)[] = [];

    dataWithReduxValues.forEach((row) => {
      row.forEach((cell) => {
        if (cell.type === "INPUT" || cell.type === "DROPDOWN") {
          const isValid = validateCellAnswer(
            cell.value,
            cell.answer,
            cell.accuracy
          );

          if (!isValid) {
            allCorrect = false;
          }

          if (cell.unique && cell.value !== null && cell.value !== undefined) {
            if (uniqueValues.includes(cell.value)) {
              allCorrect = false;
            }
            uniqueValues.push(cell.value);
          }
        }
      });
    });

    return allCorrect;
  }, [dataWithReduxValues]);

  const handleCellChange = useCallback(
    (params: Omit<CellChangeParams, "componentID">) => {
      setIsIncomplete(false);
      // Starting a new attempt: drop the previous result so it is not left
      // behind as a stale browse-mode stop (clearing a region is a removal,
      // which is not announced — see `useStatusAnnouncer`).
      clearResult();
      const cellKey = `${params.rowId}-${params.colId}`;
      setModifiedCells((prev) => {
        const updated = new Set(prev).add(cellKey);
        tableActions.updateState({
          modifiedCells: Array.from(updated),
        });
        return updated;
      });
      tableActions.updateCell({
        rowId: params.rowId,
        colId: params.colId,
        value: params.value,
      });

      onCellChange?.({
        componentID: id,
        ...params,
      });
    },
    [onCellChange, id, tableActions, clearResult]
  );

  const handleButtonClick = useCallback(
    (params: Omit<ButtonClickParams, "componentID">) => {
      onButtonClick?.({
        componentID: id,
        ...params,
      });
    },
    [onButtonClick, id]
  );

  // Declared ahead of handleSubmit (which folds it into the result
  // announcement) rather than after the render helpers, where it used to sit.
  const getFeedbackText = useCallback(
    (isCorrect: boolean): string => {
      const feedbackProp = isCorrect ? correctFeedback : incorrectFeedback;
      const defaultMessage = isCorrect
        ? messages.correctFeedbackDefault
        : messages.incorrectFeedbackDefault;

      if (typeof feedbackProp === "string") {
        return intl.formatMessage({
          id: feedbackProp,
          defaultMessage: feedbackProp,
          description: "Custom feedback message for table question",
        });
      }

      if (!isCorrect) {
        const { totalCount, incorrectCount } =
          countAnswerableCells(dataWithReduxValues);
        return intl.formatMessage(defaultMessage, {
          incorrectCount,
          totalCount,
        });
      }

      return intl.formatMessage(defaultMessage);
    },
    [intl, correctFeedback, incorrectFeedback, dataWithReduxValues]
  );

  /**
   * The full spoken result for one submit: the verdict shown on screen, then any
   * parent-supplied feedback messages, then any remaining attempts.
   *
   * Composed here rather than read back out of the DOM because the visible
   * pieces live in separate banners and the attempts counter — announcing each
   * from its own live region is exactly what made them compete and drop.
   * Everything is reduced to plain text, since a live region speaks markup
   * literally.
   *
   * Each piece is gated on the same condition that renders it, so the
   * announcement says what is on screen and nothing more: `showFeedback` and
   * `feedbackMessages` decide the verdict, and the attempts sentence follows the
   * visible counter. A question authored to withhold its verdict must withhold
   * it from screen-reader users too, or the announcement leaks a result nobody
   * else is shown. Returns "" when there is nothing to say, which clears the
   * regions rather than announcing.
   */
  const buildResultAnnouncement = useCallback(
    (isCorrect: boolean, nextAttemptsLeft: number): string => {
      // formatMessage returns an array of chunks rather than a string once the
      // resolved message carries rich-text tags, which authored content feedback
      // does; joining first is the same normalisation RichText applies before it
      // parses.
      const spoken = (value: unknown) =>
        spokenText(Array.isArray(value) ? value.join("") : String(value ?? ""));

      // When the parent supplies its own feedback messages the verdict banner is
      // not rendered (see the JSX below), so the announcement follows the same
      // rule and reads those instead of a verdict the user cannot see.
      const hasParentMessages =
        showFeedback && !!feedbackMessages && feedbackMessages.length > 0;

      // The verdict is the graded result, which is what `showFeedback` withholds.
      const verdict = !showFeedback
        ? ""
        : isPrediction
          ? intl.formatMessage(messages.predictionSubmitted)
          : hasParentMessages
            ? ""
            : getFeedbackText(isCorrect);

      const parentParts = hasParentMessages
        ? (feedbackMessages ?? []).map((message) => message.text)
        : [];

      const resultParts = [verdict, ...parentParts].map(spoken).filter(Boolean);

      // The counter is plain visible text that decrements on every submit, so it
      // is announced on the terms it renders on — `showFeedback` is deliberately
      // not one of them. Gating it on `showFeedback` too left this prop
      // combination with no submit feedback on any channel, while a sighted user
      // watched the count drop (it was a live region of its own before).
      //
      // It is dropped after a *correct* submit only when the readout already
      // says something else: the question is solved, so "attempts left" then
      // reads as noise rather than as the only thing that happened.
      const attemptsText =
        !isPrediction &&
        !hideAttemptsLeft &&
        nextAttemptsLeft > -1 &&
        (!isCorrect || resultParts.length === 0)
          ? intl.formatMessage(messages.attemptsLeft, {
              attemptsLeft: nextAttemptsLeft,
            })
          : "";

      return [...resultParts, spoken(attemptsText)].filter(Boolean).join(" ");
    },
    [
      intl,
      isPrediction,
      feedbackMessages,
      getFeedbackText,
      showFeedback,
      hideAttemptsLeft,
      spokenText,
    ]
  );

  const handleSubmit = useCallback(() => {
    // Keep the submit button enabled (consistent with the other question
    // types) but do not submit while editable cells are empty; instead flag
    // the incomplete state so the warning banner and cell warnings show.
    if (hasEmptyEditableCells(dataWithReduxValues)) {
      setIsIncomplete(true);
      return;
    }
    setIsIncomplete(false);

    if (showHint) {
      setShowHint?.({ componentID: id, showHint: false });
    }

    const isCorrect = checkAllAnswersCorrect();
    const submittedCorrect = isPrediction || isCorrect;

    // Dispatch submission action for tracking
    tableActions.submitAnswers({
      correct: submittedCorrect,
    });

    submittedHereRef.current = true;
    setLocalCorrect(submittedCorrect);

    let newAttemptsLeft = attemptsLeft;
    if (attemptsLeft > 0) {
      newAttemptsLeft = attemptsLeft - 1;
      setAttemptsLeft(newAttemptsLeft);
    }

    setIsSubmitted(true);
    setModifiedCells(new Set());
    submitAnswer?.(id, type);

    // Fold the whole result into the single question-level announcement. In
    // assessment mode nothing else speaks on submit: the banners are no longer
    // alerts and the attempts counter is no longer a live region.
    //
    // A prediction submit is different — it replaces Submit with Reset and moves
    // focus there, and ActionAnnouncer reports the button change. Writing the
    // region now would queue the result *before* that focus event, which is the
    // ordering that got the old alert swallowed, so hand it to the focus effect
    // to announce once focus has moved.
    const resultAnnouncement = buildResultAnnouncement(
      isCorrect,
      newAttemptsLeft
    );
    if (isPrediction && !!onReset) {
      pendingResultRef.current = resultAnnouncement;
    } else {
      announce(resultAnnouncement);
    }

    if (submittedCorrect) {
      setCompleted?.(true);
    }
  }, [
    showHint,
    setShowHint,
    id,
    attemptsLeft,
    submitAnswer,
    type,
    isPrediction,
    checkAllAnswersCorrect,
    setCompleted,
    tableActions,
    dataWithReduxValues,
    announce,
    buildResultAnnouncement,
    onReset,
  ]);

  const handleShowHint = useCallback(() => {
    if (!showHint) {
      setShowHint?.({ componentID: id, showHint: true });
    } else {
      showNextHint?.({ componentID: id });
    }
  }, [showHint, setShowHint, showNextHint, id]);

  const renderCell = (
    cell: TableCellData,
    rowIndex: number,
    colIndex: number
  ) => {
    const cellId = `${componentId}-cell-${rowIndex}-${colIndex}`;
    const cellKey = `${rowIndex}-${colIndex}`;

    // Check if cell has a non-default value from the data prop (set by middleware)
    // If so, ignore the modifiedCells state for this cell since it was set externally
    // Default values include: undefined, null, "", "0" (string placeholder in initial data)
    // Note: numeric 0 is NOT excluded — it's a legitimate value set by middleware via Number()
    const originalCell = data[rowIndex]?.[colIndex];
    const originalValue = originalCell?.value;
    const hasDataPropValue =
      originalCell &&
      (originalCell.type === "INPUT" || originalCell.type === "DROPDOWN") &&
      originalValue !== undefined &&
      originalValue !== null &&
      originalValue !== "" &&
      originalValue !== "0";

    // Only consider a cell as "modified by user" if it doesn't have a value from data prop
    const isCellModified = hasDataPropValue
      ? false
      : modifiedCells.has(cellKey);

    const isCorrect =
      (cell.showFeedback ?? showFeedback) &&
      validateCellAnswer(cell.value, cell.answer, cell.accuracy);

    // Header scope: a TITLE cell in the first column of a data row is a row
    // label (scope="row") so a screen reader announces just that cell's label;
    // TITLE cells in an all-header row stay column headers (scope="col").
    const row = dataWithReduxValues[rowIndex];
    const isHeaderRow =
      row?.every((c) => c.type === "TITLE" || c.type === "IMAGE_TITLE") ??
      false;
    const scope: "col" | "row" =
      cell.type === "TITLE" && colIndex === 0 && !isHeaderRow ? "row" : "col";

    // Give editable cells an accessible name derived from their column/row so
    // screen-reader users know which value they are entering (WCAG 4.1.2).
    const ariaLabel =
      cell.type === "INPUT" || cell.type === "DROPDOWN"
        ? getCellAccessibleLabel(dataWithReduxValues, rowIndex, colIndex, intl)
        : undefined;

    return renderTableCell({
      cellId,
      cell,
      rowIndex,
      colIndex,
      isCorrect,
      isPrediction,
      isSubmitted: isSubmitted && !isCellModified,
      isIncomplete,
      showFeedback,
      ariaLabel,
      striped,
      scope,
      onCellChange: handleCellChange,
      onButtonClick: handleButtonClick,
    });
  };

  const renderTable = () => (
    <Form.Root onSubmit={(e) => e.preventDefault()}>
      <div className={tableWrapperClass}>
        <table
          className={classnames(
            tableClass,
            verticalAlign === "top" && tableTopAlignClass
          )}
        >
          <tbody>
            {dataWithReduxValues.map((row, rowIndex) => (
              <tr
                key={`${componentId}-row-${rowIndex}`}
                className={tableRowClass}
              >
                {row.map((cell, colIndex) =>
                  renderCell(cell, rowIndex, colIndex)
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Form.Root>
  );

  const renderHints = () => {
    if (!hints || !showHint) return null;

    const visibleHints = hints.slice(0, (hintIndex ?? 0) + 1);

    return (
      <div className={hintAreaClass}>
        {visibleHints.map((hint, index) => (
          <p key={`hint-${index}`} className={hintTextClass}>
            {`Row: ${hint.rowId}, Col: ${hint.colId}, Feedback: ${hint.feedback}`}
          </p>
        ))}
      </div>
    );
  };

  const hasMoreHints =
    hints &&
    (hints.length > (hintIndex ?? 0) + 1 ||
      (hints.length === (hintIndex ?? 0) + 1 && !showHint));

  // Render instruction on `question` when present, otherwise fall back to `text`.
  // This prevents duplication when both content types are used together.
  // Skip entirely for read-only data tables (no editable cells).
  const showInstruction = !isReadOnly;
  const showInstructionOnQuestion =
    showInstruction && content.includes("question") && !!question;
  const showInstructionOnText = showInstruction && !showInstructionOnQuestion;

  const contentRenderers: Record<
    string,
    (index: number) => React.ReactNode | null
  > = {
    title: (index) =>
      title ? (
        <div key={`title-${index}`} className={questionTitleClass}>
          <p className={questionTextParagraphClass}>
            <RichText value={title} equation={containsMathJax(title)} />
          </p>
        </div>
      ) : null,

    text: (index) =>
      text ? (
        <div key={`text-${index}`} className={questionTextClass}>
          <BodyText size="l" equation={containsMathJax(question)}>
            {text}
          </BodyText>
          {showInstructionOnText && (
            <p className={instructionTextClass}>
              <RichText value={messages.instructionText} />
            </p>
          )}
        </div>
      ) : null,

    question: (index) =>
      question ? (
        <div key={`question-${index}`} className={questionTextClass}>
          <BodyText size="l" equation={containsMathJax(question)}>
            {question}
          </BodyText>
          {showInstructionOnQuestion && (
            <p className={instructionTextClass}>
              <RichText value={messages.instructionText} />
            </p>
          )}
        </div>
      ) : null,

    image: (index) => {
      if (!image) return null;
      const imageData = typeof image === "string" ? { src: image } : image;
      return (
        <Figure
          key={`image-${index}`}
          src={imageData.src}
          altText={imageData.altText || ""}
          caption={imageData.caption}
        />
      );
    },

    table: (index) => (
      <React.Fragment key={`table-${index}`}>{renderTable()}</React.Fragment>
    ),

    hint: (index) => {
      if (!hintDetails) return null;
      const isOpen = hintDetails.open !== undefined ? hintDetails.open : false;
      return (
        <div key={`hint-${index}`} className={collapsibleClass}>
          <Collapsible
            label={hintDetails.label}
            content={hintDetails.content}
            open={isOpen}
          />
        </div>
      );
    },
  };

  const renderContent = () => {
    const elements: React.ReactNode[] = [];

    if (showQuestionHeading) {
      elements.push(
        <QuestionHeader
          key="header"
          id={headerId}
          isPrediction={isPrediction}
          heading={heading}
        />
      );
    }

    let i = 0;
    while (i < content.length) {
      const contentType = content[i];

      // Group title and text together in a header wrapper
      if (contentType === "title" && content[i + 1] === "text") {
        const titleElement = title ? (
          <div key={`title-${i}`} className={questionTitleClass}>
            <p className={questionTextParagraphClass}>
              <RichText value={title} equation={containsMathJax(title)} />
            </p>
          </div>
        ) : null;

        const textElement = text ? (
          <div key={`text-${i + 1}`} className={questionTextClass}>
            <BodyText size="l" equation={containsMathJax(question)}>
              {text}
            </BodyText>
            {showInstructionOnText && (
              <p className={instructionTextClass}>
                <RichText value={messages.instructionText} />
              </p>
            )}
          </div>
        ) : null;

        if (titleElement || textElement) {
          elements.push(
            <div key={`header-group-${i}`} className={questionHeaderClass}>
              {titleElement}
              {textElement}
            </div>
          );
        }
        i += 2;
        continue;
      }

      const renderer = contentRenderers[contentType];
      if (renderer) {
        const element = renderer(i);
        if (element) {
          elements.push(element);
        }
      }
      i++;
    }

    return elements;
  };

  const submitButtonText = buttonText
    ? buttonText
    : isPrediction
      ? messages.submitPrediction.id
      : messages.submitAnswer.id;
  const SubmitButtonIcon = iconName ? Pencil : undefined;

  // Once a prediction is submitted the Submit button is replaced by Reset;
  // assessment mode keeps a persistent Submit for resubmission (no toggle).
  const showPredictionReset =
    isPrediction && localCorrect !== null && localCorrect !== undefined;

  // Label of the primary action button in its current state, fed to the
  // ActionAnnouncer so screen readers are told when Submit becomes Reset. The
  // auxiliary Hint button is ignored.
  const currentActionLabel = showPredictionReset
    ? intl.formatMessage(messages.resetPrediction)
    : buttonText
      ? intl.formatMessage({ id: buttonText, defaultMessage: buttonText })
      : intl.formatMessage(
          isPrediction ? messages.submitPrediction : messages.submitAnswer
        );

  return (
    <div
      className={classnames(
        isReadOnly
          ? tableReadonlyClass
          : isTransparent
            ? tableQuestionTransparentClass
            : tableQuestionClass,
        className
      )}
      data-name="Table question"
    >
      <div
        className={
          isReadOnly
            ? containerReadonlyClass
            : isTransparent
              ? containerTransparentClass
              : containerClass
        }
      >
        {/*
          The question-level status regions (see `useStatusAnnouncer`). They are
          rendered ahead of the question deliberately: a screen-reader user
          browsing the question after a submit reaches the verdict first, which is
          what the original report asked for — the verdict was previously only
          reachable after walking the whole table. They are visually hidden, so
          this costs nothing visually and the visible banner still sits with the
          footer.
        */}
        {announcementRegions}
        {renderContent()}
        {renderHints()}

        {/*
          The incomplete-submit warning keeps its own always-mounted region: it
          fires on a submit that produced no result at all, moves no focus, and so
          never competes with the result readout above.
        */}
        <div
          aria-live="polite"
          aria-atomic="true"
          className={classnames(
            liveRegionClass,
            isIncomplete && liveRegionVisibleClass
          )}
        >
          {isIncomplete && (
            <div className={feedbackMessagesContainerClass}>
              <FeedbackBanner
                variant="warning"
                message={messages.incompleteWarning}
                alertRole={false}
              />
            </div>
          )}
        </div>

        {showFeedback &&
          isSubmitted &&
          !isIncomplete &&
          !isPrediction &&
          (!feedbackMessages || feedbackMessages.length === 0) && (
            /*
              alertRole={false} on every result banner: the graded result is
              announced by the polite status region above. Left as an alert, the
              banner is inserted together with its wrapper on submit — the case
              VoiceOver reliably drops — and in prediction mode that insertion
              shares a commit with the focus move to Reset, so the alert was
              flushed by the focus announcement. The banner stays visible and
              reachable in browse mode either way.

              In assessment mode the status region is then the only thing that
              speaks on submit. Prediction mode has two more channels that are
              not this component's to remove: the focus move to Reset, and
              ActionAnnouncer reporting the button change. The result is
              announced after that focus move (see handleSubmit) so it queues
              behind them rather than being discarded by them.
            */
            <FeedbackBanner
              variant={localCorrect ? "success" : "fail"}
              alertRole={false}
              message={getFeedbackText(localCorrect as boolean)}
            />
          )}

        {showFeedback && isPrediction && isSubmitted && !isIncomplete && (
          <FeedbackBanner
            variant="information"
            alertRole={false}
            message={messages.predictionSubmitted}
          />
        )}

        {showFeedback &&
          !isIncomplete &&
          feedbackMessages &&
          feedbackMessages.length > 0 && (
            <div className={feedbackMessagesContainerClass}>
              {feedbackMessages.map((message) => (
                <FeedbackBanner
                  key={message.id}
                  variant={message.type ?? "success"}
                  alertRole={false}
                  message={message.text}
                />
              ))}
            </div>
          )}

        {!hideFooter && !isDataTable && (
          <div className={footerClass} ref={footerRef}>
            <ActionAnnouncer label={currentActionLabel} />
            {attemptsLeft > -1 && !hideAttemptsLeft && (
              // Plain visible text, deliberately NOT a live region: the
              // remaining attempts are folded into the single question-level
              // result announcement (see buildResultAnnouncement), so a second
              // polite region here would compete with it for the screen
              // reader's attention.
              <p className={attemptsTextClass}>
                <RichText
                  value={{
                    ...messages.attemptsLeft,
                    placeholderValues: { attemptsLeft },
                  }}
                />
              </p>
            )}

            {hasMoreHints && (
              <LxButton
                variant="secondary"
                text={intl.formatMessage(
                  showHint ? messages.nextHint : messages.showHint
                )}
                size="large"
                onClickHandler={handleShowHint}
              />
            )}

            {isPrediction &&
              localCorrect !== null &&
              localCorrect !== undefined &&
              onReset && (
                <LxButton
                  variant="secondary"
                  text={intl.formatMessage(messages.resetPrediction)}
                  size="large"
                  testId="reset-prediction"
                  onClickHandler={() => {
                    setIsSubmitted(false);
                    tableActions.updateState({
                      isSubmitted: false,
                    });
                    clearResult();
                    onReset();
                  }}
                />
              )}

            {!(
              isPrediction &&
              localCorrect !== null &&
              localCorrect !== undefined
            ) && (
              <LxButton
                variant={isPrediction ? "alternative" : "primary"}
                text={submitButtonText}
                size="large"
                onClickHandler={handleSubmit}
                Icon={SubmitButtonIcon}
                disabled={submitDisabled}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* -------------------------------- Exported Component -------------------------------- */

export const TableQuestion = (props: TableQuestionProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <TooltipProvider delayDuration={300} skipDelayDuration={200}>
      <TableQuestionInternal {...props} />
    </TooltipProvider>
  </TranslationsProvider>
);
